import React, { useEffect, useState } from "react";

import styles from "./CreateForm.module.scss";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { GiCancel } from "react-icons/gi";
import { SubmitHandler, useForm } from "react-hook-form";
import { IFormInput } from "../../types/salaries";
import { toastStyles } from "../../static/styles";
import { GET } from "../../services/axios.service";
import moment from "moment";
import { FaCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  getTotalPayment,
  getTotalSalary,
  getTotalWithdrawal,
  toggleCreateModal,
} from "../../features/salaries/salariesSlice";

type Props = {
  handleCreateSalaries: (data: IFormInput) => void;
};

const CreateForm = ({ handleCreateSalaries }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const totalSalary = useAppSelector(getTotalSalary);
  const totalPayment = useAppSelector(getTotalPayment);
  const totalWithdrawal = useAppSelector(getTotalWithdrawal);

  // બાકી ઉપાડ (outstanding advance) - the same expression the server used to
  // return, derived from the totals the ledger already fetched.
  const pendingWithdrawal = totalWithdrawal + totalPayment - totalSalary;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [lastTotalWithdrawal, setLastTotalWithdrawal] =
    useState<number>(pendingWithdrawal);
  const [lastTotalAmount, setLastTotalAmount] = useState<number>(0);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>();

  const recalculateAmounts = () => {
    const { amount, roundoff_amount, withdrawal_deposit } = getValues();

    const total =
      parseFloat(`${amount || 0}`) + parseFloat(`${roundoff_amount || 0}`);
    setTotalAmount(total);

    setLastTotalWithdrawal(
      parseFloat(`${pendingWithdrawal || 0}`) -
        parseFloat(`${withdrawal_deposit || 0}`)
    );

    setLastTotalAmount(
      parseFloat(`${total || 0}`) - parseFloat(`${withdrawal_deposit || 0}`)
    );
  };

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (id) {
      setIsLoading(true);
      await handleCreateSalaries({
        ...data,
        employee_id: parseInt(id),
        status: 0,
      });
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
  };

  const handleChangeEmployee = async (id: string) => {
    await GET(`/v1/admin/salaries/employees/${id}/pending-salary`, {})
      .then(async (response: any) => {
        if (response.data.status === "success") {
          const pendingSalary = (response.data.data || [])[0];

          if (pendingSalary) {
            setValue(
              "fromDate",
              moment(pendingSalary.from_date).format("YYYY-MM-DD")
            );
            setValue(
              "toDate",
              moment(pendingSalary.to_date).format("YYYY-MM-DD")
            );
          }
        }
      })
      .catch((error: any) => {
        toast.error(error.data.message, {
          ...(toastStyles as any),
        });
      });
  };

  useEffect(() => {
    handleChangeEmployee(id || "0");
  }, [id]);

  useEffect(() => {
    recalculateAmounts();
  }, [pendingWithdrawal]);

  return (
    <div className={styles.CreateForm}>
      <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-[80vw] lg:h-fit lg:min-h-[calc(100vh-80px)] shadow-lg lg:rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-2xl mt-10 lg:mt-0 text-center lg:text-left font-bold leading-6 text-primary-1">
            હિસાબ ની માહિતી
          </h3>

          <GiCancel
            onClick={handleCloseModal}
            className="absolute cursor-pointer z-10 shadow bg-white top-3 right-5 text-3xl lg:text-xl text-primary-1 border-2 border-primary-1 p-1 lg:p-0 rounded-full"
          />
          <div className="mt-2 py-3 lg:py-2">
            <form onSubmit={handleSubmit(onSubmit)} className="p-4 pt-1 pb-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <label className="text-primary-1 font-bold">તારીખ થી :</label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("fromDate", {
                        required: true,
                      })}
                      placeholder="From Date"
                    />
                  </div>

                  {errors?.fromDate && (
                    <span className="text-red-500">તારીખ સિલેક્ટ કરો</span>
                  )}
                </div>

                <div>
                  <label className="text-primary-1 font-bold">
                    તારીખ સુધી :
                  </label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1  placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("toDate", {
                        required: true,
                      })}
                      placeholder="To Date"
                    />
                  </div>

                  {errors?.toDate && (
                    <span className="text-red-500">તારીખ સિલેક્ટ કરો</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    હિસાબ અમાઉન્ટ :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-11 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      pattern="-?[0-9]*"
                      step={"any"}
                      placeholder="હિસાબ અમાઉન્ટ દાખલ કરો"
                      {...register("amount", { required: true, min: 0 })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                      onKeyUp={() => {
                        recalculateAmounts();
                        return true;
                      }}
                    />
                  </div>
                  {errors.amount && (
                    <span className="text-red-500">
                      હિસાબ અમાઉન્ટ દાખલ કરો
                    </span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    રાઉન્ડ ઑફ :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-11 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      pattern="-?[0-9]*"
                      step={"any"}
                      placeholder="રાઉન્ડ ઑફ દાખલ કરો"
                      {...register("roundoff_amount", { required: false })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                      onKeyUp={() => {
                        recalculateAmounts();
                        return true;
                      }}
                    />
                  </div>
                  {errors.roundoff_amount &&
                    errors.roundoff_amount.type === "required" && (
                      <span className="text-red-500">રાઉન્ડ ઑફ દાખલ કરો</span>
                    )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    કપાત ઉપાડ :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-11 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      pattern="-?[0-9]*"
                      step={"any"}
                      placeholder="કપાત ઉપાડ દાખલ કરો"
                      {...register("withdrawal_deposit", {
                        required: false,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                      onKeyUp={() => {
                        recalculateAmounts();
                        return true;
                      }}
                    />
                  </div>
                  {errors.withdrawal_deposit &&
                    errors.withdrawal_deposit.type === "required" && (
                      <span className="text-red-500">કપાત ઉપાડ દાખલ કરો</span>
                    )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    હિસાબ ટોટલ અમાઉન્ટ :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-11 w-full relative rounded">
                    <input
                      value={`${(totalAmount || 0)?.toFixed(2)}`}
                      className="text-primary-1 bg-gray-300 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="text"
                      readOnly
                    />
                  </div>
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    ટોટલ ઉપાડ :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-11 w-full relative rounded">
                    <input
                      value={`${(pendingWithdrawal || 0)?.toFixed(2)}`}
                      className="text-primary-1 bg-gray-300 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="text"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <h3 className="text-2xl mt-6 text-left font-bold leading-8 text-primary-1">
                બાકી ઉપાડ અમાઉન્ટ - Rs.{" "}
                {lastTotalWithdrawal ? lastTotalWithdrawal?.toFixed(2) : "0.00"}
              </h3>

              <h3 className="text-2xl mt-6 text-left font-bold leading-8 text-primary-1">
                પગાર અમાઉન્ટ - Rs.{" "}
                {lastTotalAmount ? lastTotalAmount?.toFixed(2) : "0.00"}
              </h3>

              <div className="mt-4 text-center">
                <button
                  className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                </button>
              </div>

              <div className="mt-4 text-center">
                <button
                  className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  બંધ કરો
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateForm;
